/**
 * Minimal Heather API client for the admin panel.
 *
 * The /messages endpoint is POST-with-SSE, which EventSource cannot do,
 * so we use fetch + ReadableStream and parse the SSE frames by hand.
 */
import { env } from '../env';

const API = `${env.VITE_API_URL}/api/admin/heather`;

export interface HeatherRunSummary {
  id: string;
  status: string;
  createdAt: string;
}

export interface HeatherSseEvent {
  event: string;
  data: unknown;
}

export interface ConfirmCard {
  toolCallId: string;
  toolName: string;
  input: unknown;
  humanSummary: string;
}

export async function createRun(): Promise<HeatherRunSummary> {
  const r = await fetch(`${API}/runs`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

interface MessageBody {
  message?: string;
  resumeToolCallId?: string;
}

/**
 * Stream the response for a user message (or a resume-after-confirm).
 * Yields parsed SSE events one at a time.
 */
export async function* streamMessage(
  runId: string,
  body: MessageBody,
  signal: AbortSignal,
): AsyncGenerator<HeatherSseEvent> {
  const r = await fetch(`${API}/runs/${runId}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!r.ok || !r.body) {
    const text = await r.text().catch(() => '');
    throw new Error(`heather: ${r.status} ${r.statusText} ${text}`);
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by \n\n.
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const parsed = parseFrame(frame);
      if (parsed) yield parsed;
    }
  }
  if (buffer.trim()) {
    const parsed = parseFrame(buffer);
    if (parsed) yield parsed;
  }
}

function parseFrame(frame: string): HeatherSseEvent | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('event: ')) event = line.slice(7).trim();
    else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
  }
  if (dataLines.length === 0) return null;
  const raw = dataLines.join('\n');
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    data = raw;
  }
  return { event, data };
}

export async function confirmToolCall(
  runId: string,
  toolCallId: string,
  action: 'approve' | 'reject',
): Promise<void> {
  const r = await fetch(`${API}/runs/${runId}/confirm`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ toolCallId, action }),
  });
  if (!r.ok) throw new Error(await r.text());
}

export async function cancelRun(runId: string): Promise<void> {
  await fetch(`${API}/runs/${runId}/cancel`, {
    method: 'POST',
    credentials: 'include',
  });
}
